import { ReactNode, useState } from 'react';

import { usePlaybookStore } from '@app/store/zustand-store/use-playbook-store';

import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectIcon,
} from '@app/components/select-compound';
import Field from '@app/components/field';
import { FormLabel } from '@app/components/form';
import ModalForm from '@app/hocs/modal-form';
import EditIcon from '@app/components/icons/edit';
import Button from '@app/components/button';
import { T, t } from '@app/components/translation';
import { CancelButton } from '@app/components/buttons';
import TextArea from '@app/components/text-area';
import toast from '@app/components/toasts';
import { CRITICAL, MISSING, WARNING, FOUND, INFO } from '../../constants';

import style from './edit-modal.module.scss';

const parseNullToEmptyString = (value: string | null | void) => (!value ? '' : value);

type Values = {
  reviewType: string;
  feedbackTextWhatToDo: string;
  feedbackTextExample: string;
  breachSeverityWhenFound: string;
  breachSeverityWhenMissing: string;
  valueOperator?: string;
  [key: string]: any;
};

type Props = {
  initialValues: Values;
  title: ReactNode;
  children?: ReactNode;
  name: string;
};

const severityOptions = [
  {
    label: (
      <T
        _str="Issue"
        _comment="Label for when a breaching clause in a document is a small risk to the user."
      />
    ),
    value: CRITICAL,
  },
  {
    label: (
      <T
        _str="Risk"
        _comment="Label for when a breaching clause in a document is a critical risk to the user."
      />
    ),
    value: WARNING,
  },
];

const reviewType = [
  {
    label: (
      <T
        _str="Review Clause"
        _comment="label for when AI review should expect to find a clause in a document"
      />
    ),
    value: FOUND,
  },
  {
    label: (
      <T
        _str="The clause shouldn't be present in the document"
        _comment="Label for when AI review should not expect to find this clause in a document"
      />
    ),
    value: MISSING,
  },
];

const EditModalComponent = ({ name, title, initialValues, children }: Props) => {
  const setPlaybookValues = usePlaybookStore((state) => state.setPlaybookValues);
  const [reviewTypeValue, setReviewTypeValue] = useState(initialValues.reviewType);

  const isReviewTypeFound = reviewTypeValue === FOUND;

  const handleSubmit = (submittedValues: Values, closeConfirmation: () => void) => {
    let updatedValues = {};

    if (reviewTypeValue === FOUND) {
      updatedValues = {
        ...updatedValues,
        breachSeverityWhenFound: INFO,
        breachSeverityWhenMissing:
          submittedValues.breachSeverityWhenMissing === INFO
            ? WARNING
            : submittedValues.breachSeverityWhenMissing,
      };
    }
    if (reviewTypeValue === MISSING) {
      updatedValues = {
        ...updatedValues,
        breachSeverityWhenMissing: INFO,
        breachSeverityWhenFound:
          submittedValues.breachSeverityWhenFound === INFO
            ? WARNING
            : submittedValues.breachSeverityWhenFound,
      };
    }
    if (name === 'governingLaw' && reviewTypeValue === FOUND) {
      const governingLawValues = submittedValues.governingLaw.map(
        (code: { value: string }) => code.value,
      );
      updatedValues = {
        ...updatedValues,
        value: governingLawValues,
      };
    }

    if (name === 'paymentTerm' && reviewTypeValue === FOUND) {
      const paymentTermValue = `P${submittedValues.paymentTerm}D`;
      updatedValues = {
        ...updatedValues,
        value: paymentTermValue,
        valueOperator: submittedValues.valueOperator,
      };
    }

    if (name === 'disputeResolution' && reviewTypeValue === FOUND) {
      updatedValues = {
        ...updatedValues,
        value:
          submittedValues.disputeResolution === 'LABEL_DECLINE'
            ? []
            : [
                submittedValues.disputeResolution,
              ],
      };
    }

    setPlaybookValues({
      [name]: {
        ...updatedValues,
        feedbackTextExample: submittedValues.feedbackTextExample,
        feedbackTextWhatToDo: submittedValues.feedbackTextWhatToDo,
      },
    });

    closeConfirmation();

    toast.success({
      id: 'clause-updated',
      title: (
        <T
          _str="Clause updated"
          _comment="Toast message when a clause is successfully updated."
        />
      ),
      duration: 5000,
    });
  };

  const renderBody = () => {
    return (
      <div>
        <p className={style.Title}>{title}</p>
        <div
          style={{
            marginBottom: 16,
          }}
        >
          <FormLabel htmlFor="reviewType">
            <T
              _str="Review Type"
              _comment="label for review type field in edit playbook modal."
            />
          </FormLabel>
          <Field
            name="reviewType"
            label={t('Review type', {
              _comment: 'label for review type field in edit playbook modal.',
            })}
            required
            render={({ input: { onChange } }) => (
              <Select
                value={reviewTypeValue}
                onValueChange={(value) => {
                  onChange(value);
                  setReviewTypeValue(value);
                }}
              >
                <SelectTrigger
                  id="reviewType"
                  className={style.ReviewTypeTrigger}
                >
                  <SelectValue />
                  <SelectIcon />
                </SelectTrigger>
                <SelectContent
                  style={{
                    zIndex: 10002,
                  }}
                >
                  {reviewType.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <Field
          component={TextArea}
          label={
            <T
              _str="Feedback text"
              _comment="Label for the feedback text field in the edit modal."
            />
          }
          labelCustomClass={style.LabelModal}
          name="feedbackTextWhatToDo"
          resize="vertical"
          parse={parseNullToEmptyString}
        />
        {reviewTypeValue === FOUND && (
          <Field
            component={TextArea}
            label={
              <T
                _str="Example clause text"
                _comment="Label for the feedback text field in the edit modal."
              />
            }
            labelCustomClass={style.LabelModal}
            name="feedbackTextExample"
            resize="vertical"
            parse={parseNullToEmptyString}
          />
        )}
        {reviewTypeValue === FOUND && (
          <div
            style={{
              marginBottom: 16,
            }}
          >
            <FormLabel htmlFor="breachSeverityWhenMissing">
              <T
                _str="Severity if clause is missing from document"
                _comment="Label for breach severity in edit playbook modal."
              />
            </FormLabel>
            <Field
              name="breachSeverityWhenMissing"
              label={
                <T
                  _str="Severity if clause is missing from document"
                  _comment="Label for breach severity in edit playbook modal."
                />
              }
              required
              render={({ input: { onChange, value } }) => {
                const initialValue = value === INFO ? WARNING : value;
                return (
                  <Select
                    value={initialValue || undefined}
                    onValueChange={onChange}
                  >
                    <SelectTrigger
                      id="breachSeverityWhenMissing"
                      className={style.SeverityTrigger}
                    >
                      <SelectValue />
                      <SelectIcon />
                    </SelectTrigger>
                    <SelectContent
                      style={{
                        zIndex: 10002,
                      }}
                    >
                      {severityOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </div>
        )}
        {reviewTypeValue === MISSING && (
          <div
            style={{
              marginBottom: 16,
            }}
          >
            <FormLabel htmlFor="breachSeverityWhenFound">
              <T
                _str="Severity if clause is present in document"
                _comment="Label for breach severity in edit playbook modal."
              />
            </FormLabel>
            <Field
              name="breachSeverityWhenFound"
              label={
                <T
                  _str="Severity if clause is present in document"
                  _comment="Label for breach severity in edit playbook modal."
                />
              }
              required
              render={({ input: { onChange, value } }) => {
                const initialValue = value === INFO ? WARNING : value;
                return (
                  <Select
                    value={initialValue || undefined}
                    onValueChange={onChange}
                  >
                    <SelectTrigger
                      id="breachSeverityWhenFound"
                      className={style.SeverityTrigger}
                    >
                      <SelectValue />
                      <SelectIcon />
                    </SelectTrigger>
                    <SelectContent
                      style={{
                        zIndex: 10002,
                      }}
                    >
                      {severityOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </div>
        )}
        {isReviewTypeFound && children}
      </div>
    );
  };

  const renderEditButton = (onClick: React.MouseEventHandler) => (
    <Button
      onClick={onClick}
      icon={<EditIcon height="14px" />}
    >
      <T
        _str="Edit"
        _comment="Edit element for user to update and edit a data field."
      />
    </Button>
  );

  const renderActions = ({
    closeConfirmation,
    formProps,
  }: {
    closeConfirmation: () => void;
    formProps: any;
  }) => {
    return (
      <div className={style.ActionsContainer}>
        <CancelButton onClick={closeConfirmation} />
        <Button
          kind="primary"
          disabled={formProps.invalid}
          onClick={() => handleSubmit(formProps.values, closeConfirmation)}
        >
          <T
            _str="Confirm"
            _comment="CTA button text"
          />
        </Button>
      </div>
    );
  };

  return (
    <ModalForm
      title={
        <T
          _str="Edit clause"
          _comment="Header in the modal for editing a clause in the playbook."
        />
      }
      body={renderBody()}
      onSubmit={handleSubmit}
      actions={renderActions}
      formState={{}}
      initialValues={initialValues}
    >
      {renderEditButton}
    </ModalForm>
  );
};

export const EditModal = EditModalComponent;
